import { setGA4CustomEvent } from "@iewc/Store/Handlers/Utilities/CustomTracking";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC, useEffect, useRef, useState } from "react";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    openInNewWindow = "openInNewWindow",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.openInNewWindow]: boolean;
        [fields.customCSS]: string;
    };
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
}

export const imageStyles: ImageStyles = {
    image: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
    img: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
};

export const Image: FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.imageLink);
    const styles = useMergeStyles("image", imageStyles);

    // Verndale Customization
    const containerRefGA4 = useRef<HTMLImageElement>(null);
    const [isGA4Logo, setIsGA4Logo] = useState("");

    const image = <Img className="img" src={fields.imageUrl} altText={fields.altText} loading="lazy" {...styles.img} />;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    /* Verndale Custom Feature - Trigger GA4 datalayer with widget cta information */
    const onLinkClickHandler = () => {
        setGA4CustomEvent({
            event: "image_widget_link",
            click_url: url,
        });
    };

    /* 
    Verndale Customization 
    we need to determine if the parent parent recursively has a classname logo-item to determine 
    if the image is a logo that needs to be tracked by GA4 
    */

    useEffect(() => {
        if (containerRefGA4.current) {
            const parentWithClass = findParentWithClass(containerRefGA4.current, "logo-item");
            // we need to get the class name number because the class name is dynamic

            const className = parentWithClass?.className
                .split(" ")
                .find(c => c.includes("logo-item--"))
                ?.split("--")[1];
            if (className) {
                setIsGA4Logo(`verndaleGA4__logo-item--${className}`);
            }
        }
    }, [containerRefGA4]);

    const findParentWithClass = (element: HTMLElement | null, className: string): HTMLElement | null => {
        // If the element is null, we've reached the top of the DOM tree without finding the class
        if (!element) {
            return null;
        }

        // If the element has the class, return the element
        if (element.classList.contains(className)) {
            return element;
        }

        // If the element doesn't have the class, check its parent
        return findParentWithClass(element.parentElement, className);
    };

    // END Verndale Customization

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="img-wrapper" {...styles.wrapper} ref={containerRefGA4}>
                {url ? (
                    <Link
                        className={`img-link ${isGA4Logo}`}
                        href={url}
                        {...styles.imageLink}
                        target={fields.openInNewWindow ? "_blank" : "_self"}
                        /* Verndale Custom Feature */
                        onClick={onLinkClickHandler}
                    >
                        {image}
                    </Link>
                ) : (
                    image
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.img-wrapper {
}

.img-link {
}

.img {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Image,
    definition: {
        group: "Basic",
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.imageUrl,
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.imageLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: "openInNewWindow",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                displayName: "Open in new window",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
